import React, { useState } from 'react';
import { ArrowRight, FileText, Image, File, Plus, RefreshCw, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const EditRequestDiff = ({ editRequestId, originalValues, requestedChanges, documentChanges }) => {
  const [downloadingDoc, setDownloadingDoc] = useState(null);
  // Field label mapping for better display
  const fieldLabels = {
    name: 'Permit Holder Name',
    father_name: "Father's Name",
    village: 'Village/Ward',
    post_office: 'Post Office',
    police_station: 'Police Station',
    size_sqft: 'Land Size (sq ft)',
    validity_date: 'Validity Date',
    north_boundary: 'North Boundary',
    south_boundary: 'South Boundary',
    east_boundary: 'East Boundary',
    west_boundary: 'West Boundary',
    location_description: 'Location Description',
    remarks: 'Remarks',
    boundaryPoints: 'Boundary Points'
  };

  const formatValue = (value, field) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-400 italic">Not set</span>;
    }

    if (field === 'validity_date') {
      return new Date(value).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }

    if (field === 'boundaryPoints') {
      if (Array.isArray(value)) {
        return `${value.length} boundary points`;
      }
      return value;
    }

    return String(value);
  };

  const getFileIcon = (mimetype) => {
    if (mimetype?.includes('pdf')) {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    if (mimetype?.includes('image')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    }
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle secure document viewing
  const handleViewDocument = async (documentType, index = null) => {
    try {
      const docId = documentType === 'landRegistry' ? 'landRegistry' :
                    documentType === 'registryMap' ? 'registryMap' :
                    `supporting-${index}`;
      setDownloadingDoc(docId);

      // Build query parameters
      const params = new URLSearchParams({ documentType });
      if (index !== null) {
        params.append('index', index);
      }

      // Call API to get pre-signed URL
      const response = await api.get(`/edit-requests/${editRequestId}/documents/download?${params}`);

      if (response.data.success) {
        // Open the pre-signed URL in a new tab
        window.open(response.data.downloadUrl, '_blank');
        toast.success(`Opening ${response.data.fileName}`);
      } else {
        toast.error('Failed to generate download link');
      }
    } catch (error) {
      console.error('Document view error:', error);
      toast.error(error.response?.data?.message || 'Failed to view document');
    } finally {
      setDownloadingDoc(null);
    }
  };

  // Get field changes
  const fieldChanges = [];
  if (requestedChanges && Object.keys(requestedChanges).length > 0) {
    Object.entries(requestedChanges).forEach(([field, newValue]) => {
      const oldValue = originalValues?.[field];
      // Only include if value actually changed
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        fieldChanges.push({
          field,
          label: fieldLabels[field] || field,
          oldValue,
          newValue
        });
      }
    });
  }

  const hasFieldChanges = fieldChanges.length > 0;
  const hasDocumentChanges = documentChanges && (
    documentChanges.landRegistryDocument ||
    documentChanges.registryMapDocument ||
    documentChanges.scannedDocuments?.length > 0
  );

  if (!hasFieldChanges && !hasDocumentChanges) {
    return (
      <div className="text-center py-8 text-gray-500">
        No changes to display
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Field Changes */}
      {hasFieldChanges && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Field Changes ({fieldChanges.length})
          </h4>
          <div className="border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 gap-4 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider">
              <div>Field</div>
              <div>Current Value</div>
              <div>New Value</div>
            </div>
            {/* Changes */}
            <div className="divide-y divide-gray-200">
              {fieldChanges.map((change, index) => (
                <div key={change.field} className="grid grid-cols-3 gap-4 px-4 py-3 items-start">
                  <div className="font-medium text-gray-900 text-sm">
                    {change.label}
                  </div>
                  <div className="bg-red-50 p-2 rounded text-sm">
                    <span className="text-red-700 line-through">
                      {formatValue(change.oldValue, change.field)}
                    </span>
                  </div>
                  <div className="bg-green-50 p-2 rounded text-sm flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-green-700 font-medium">
                      {formatValue(change.newValue, change.field)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Document Changes */}
      {hasDocumentChanges && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Document Changes
          </h4>
          <div className="border rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {/* Land Registry Document */}
              {documentChanges.landRegistryDocument && (
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getFileIcon(documentChanges.landRegistryDocument.mimetype)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">Land Registry Document</p>
                        <p className="text-xs text-gray-500">
                          {documentChanges.landRegistryDocument.originalName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Replace
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatFileSize(documentChanges.landRegistryDocument.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleViewDocument('landRegistry')}
                        disabled={downloadingDoc === 'landRegistry'}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-wait"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        {downloadingDoc === 'landRegistry' ? 'Loading...' : 'View'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Registry Map Document */}
              {documentChanges.registryMapDocument && (
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getFileIcon(documentChanges.registryMapDocument.mimetype)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">Registry Map Document</p>
                        <p className="text-xs text-gray-500">
                          {documentChanges.registryMapDocument.originalName}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-3">
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Replace
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatFileSize(documentChanges.registryMapDocument.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleViewDocument('registryMap')}
                        disabled={downloadingDoc === 'registryMap'}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-wait"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        {downloadingDoc === 'registryMap' ? 'Loading...' : 'View'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Supporting Documents */}
              {documentChanges.scannedDocuments?.length > 0 && (
                <div className="px-4 py-3">
                  <div className="mb-2">
                    <p className="text-sm font-medium text-gray-900">
                      Supporting Documents ({documentChanges.scannedDocuments.length} new)
                    </p>
                  </div>
                  <div className="space-y-2">
                    {documentChanges.scannedDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 rounded p-2">
                        <div className="flex items-center gap-2">
                          {getFileIcon(doc.mimetype)}
                          <span className="text-sm text-gray-700">{doc.originalName}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{formatFileSize(doc.size)}</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <Plus className="h-3 w-3 mr-0.5" />
                            New
                          </span>
                          <button
                            onClick={() => handleViewDocument('supporting', index)}
                            disabled={downloadingDoc === `supporting-${index}`}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-wait"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {downloadingDoc === `supporting-${index}` ? 'Loading...' : 'View'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditRequestDiff;
